import type { ErrorEvent, EventHint, StackFrame } from "@sentry/react";

/**
 * Script origins that are never ours.
 *
 * `webkit-masked-url:` is WebKit's placeholder for a script whose real URL it
 * deliberately hides: Safari Web Extension content scripts and `WKUserScript`s
 * injected by iOS in-app browsers. Our own bundle always reports as
 * `https://<host>/static/js/...`, so a stack made up entirely of these frames
 * cannot come from application code.
 */
const THIRD_PARTY_SCRIPT_SCHEMES = [
  /^webkit-masked-url:/i,
  /^chrome-extension:/i,
  /^moz-extension:/i,
  /^safari-extension:/i,
  /^safari-web-extension:/i,
  /^ms-browser-extension:/i,
];

/**
 * The script URL a frame points at, or `undefined` when it carries no origin
 * information — the placeholders Sentry itself skips when deciding which URL
 * an event "belongs" to (see `_getLastValidUrl` in `@sentry/core`'s
 * InboundFilters integration). `abs_path` is a fallback for stacktrace
 * payloads that omit `filename`.
 */
function frameOrigin(frame: StackFrame | undefined): string | undefined {
  const origin = frame?.filename ?? frame?.abs_path;
  if (!origin || origin === "<anonymous>" || origin === "[native code]") {
    return undefined;
  }
  return origin;
}

/**
 * True when every frame that names a script comes from an injected/extension
 * script — e.g. the `URL.parse is not a function` rejections thrown by a
 * Safari extension on iOS 17 (Sentry HAR-AI-SEARCH-NW), whose only frame is
 * `webkit-masked-url://hidden/`.
 *
 * Requires at least one usable frame, and keeps mixed stacks: if application
 * code appears anywhere in the trace the error is still ours to look at. An
 * event with no stacktrace is never dropped here — we must not start silently
 * swallowing errors that merely failed to produce frames.
 */
export function isThirdPartyOnlyEvent(event: ErrorEvent): boolean {
  const values = event.exception?.values;
  if (!values || values.length === 0) {
    return false;
  }

  let sawUsableFrame = false;

  for (const value of values) {
    for (const frame of value.stacktrace?.frames ?? []) {
      const origin = frameOrigin(frame);
      if (!origin) {
        continue;
      }
      if (!THIRD_PARTY_SCRIPT_SCHEMES.some((scheme) => scheme.test(origin))) {
        return false;
      }
      sawUsableFrame = true;
    }
  }

  return sawUsableFrame;
}

/**
 * True for the synthetic `unhandledrejection` CustomEvents that browser
 * extensions and injected third-party scripts dispatch on `window`
 * (`isTrusted: false`, no `.reason`). Sentry's global handler can't extract a
 * reason and reports them as "…captured as promise rejection with keys:
 * [object has no keys]". A genuine unhandled rejection surfaces its `reason`,
 * so a raw DOM Event is only noise when that reason is missing.
 */
export function isSyntheticRejectionEvent(originalException: unknown): boolean {
  if (typeof Event === "undefined" || !(originalException instanceof Event)) {
    return false;
  }
  return (originalException as { reason?: unknown }).reason == null;
}

/** Composed `beforeSend` predicate: true means "don't report this event". */
export function shouldDropSentryEvent(
  event: ErrorEvent,
  hint?: EventHint,
): boolean {
  return (
    isSyntheticRejectionEvent(hint?.originalException) ||
    isThirdPartyOnlyEvent(event)
  );
}
